import { RouteLocationSchema, type RouteLocation } from '../data/schema';

type RouteListener = (route: RouteLocation) => void;

interface RoutePattern {
  name: RouteLocation['name'];
  pattern: RegExp;
  parameters: string[];
}

const ROUTE_PATTERNS: RoutePattern[] = [
  {
    name: 'read-paragraph',
    pattern: /^\/read\/([^/]+)\/(p_[A-Za-z0-9]+)\/?$/,
    parameters: ['talkId', 'paragraphId'],
  },
  {
    name: 'read-talk',
    pattern: /^\/read\/([^/]+)\/?$/,
    parameters: ['talkId'],
  },
  {
    name: 'read-index',
    pattern: /^\/read\/?$/,
    parameters: [],
  },
  {
    name: 'scripture',
    pattern: /^\/scripture\/([^/]+)\/([^/]+)\/([^/]+)\/?$/,
    parameters: ['bookId', 'chapter', 'selection'],
  },
  {
    name: 'thread',
    pattern: /^\/threads\/([^/]+)\/?$/,
    parameters: ['threadId'],
  },
  {
    name: 'threads-index',
    pattern: /^\/threads\/?$/,
    parameters: [],
  },
  {
    name: 'explore-view',
    pattern: /^\/explore\/([^/]+)\/?$/,
    parameters: ['view'],
  },
  {
    name: 'explore-index',
    pattern: /^\/explore\/?$/,
    parameters: [],
  },
  {
    name: 'search',
    pattern: /^\/search\/?$/,
    parameters: [],
  },
  {
    name: 'study',
    pattern: /^\/study\/?$/,
    parameters: [],
  },
  {
    name: 'fsy-month',
    pattern: /^\/fsy\/([^/]+)\/?$/,
    parameters: ['monthId'],
  },
  {
    name: 'fsy-index',
    pattern: /^\/fsy\/?$/,
    parameters: [],
  },
  {
    name: 'home',
    pattern: /^\/$/,
    parameters: [],
  },
];

function decodePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function matchRoute(pathname: string): RouteLocation {
  for (const route of ROUTE_PATTERNS) {
    const match = route.pattern.exec(pathname);
    if (!match) {
      continue;
    }

    const params = Object.fromEntries(
      route.parameters.map((parameter, index) => [
        parameter,
        decodePathSegment(match[index + 1] ?? ''),
      ]),
    );

    return RouteLocationSchema.parse({
      name: route.name,
      pathname,
      params,
    });
  }

  return RouteLocationSchema.parse({
    name: 'not-found',
    pathname,
    params: {},
  });
}

export class BrowserRouter {
  private readonly listeners = new Set<RouteListener>();
  private started = false;
  private currentRoute: RouteLocation;

  constructor(private readonly browserWindow: Window) {
    this.currentRoute = matchRoute(browserWindow.location.pathname);
  }

  private readonly handlePopState = (): void => {
    this.currentRoute = matchRoute(this.browserWindow.location.pathname);
    this.notify();
  };

  private readonly handleDocumentClick = (event: MouseEvent): void => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const anchor = target.closest<HTMLAnchorElement>('a[data-router]');
    if (!anchor || anchor.target || anchor.hasAttribute('download')) {
      return;
    }

    const destination = new URL(anchor.href, this.browserWindow.location.href);
    if (destination.origin !== this.browserWindow.location.origin) {
      return;
    }

    event.preventDefault();
    this.navigate(`${destination.pathname}${destination.search}${destination.hash}`);
  };

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    this.browserWindow.addEventListener('popstate', this.handlePopState);
    this.browserWindow.document.addEventListener('click', this.handleDocumentClick);
    this.notify();
  }

  stop(): void {
    if (!this.started) {
      return;
    }

    this.started = false;
    this.browserWindow.removeEventListener('popstate', this.handlePopState);
    this.browserWindow.document.removeEventListener('click', this.handleDocumentClick);
  }

  navigate(href: string): void {
    const destination = new URL(href, this.browserWindow.location.href);
    this.browserWindow.history.pushState({}, '', destination);
    this.currentRoute = matchRoute(destination.pathname);
    this.notify();
  }

  subscribe(listener: RouteListener): () => void {
    this.listeners.add(listener);
    listener(this.currentRoute);

    return () => {
      this.listeners.delete(listener);
    };
  }

  get current(): RouteLocation {
    return this.currentRoute;
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.currentRoute);
    }
  }
}
